import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';

interface DataStream {
  icon: string;
  label: string;
  value?: number; // Optional value override (0-100)
}

interface DataStreamsRadarProps {
  dataStreams: DataStream[];
  partnerPrimaryColor?: string;
  partnerSecondaryColor?: string;
}

export const DataStreamsRadar: React.FC<DataStreamsRadarProps> = ({
  dataStreams,
  partnerPrimaryColor = '#B100FF',
  partnerSecondaryColor,
}) => {
  // Transform data streams into radar chart format
  const chartData = useMemo(() => 
    dataStreams.map((stream) => ({
      category: stream.label,
      icon: stream.icon,
      value: stream.value ?? 95,
    })),
    [dataStreams]
  );

  // Chart configuration for shadcn/ui
  const chartConfig: ChartConfig = useMemo(() => ({
    value: {
      label: "Capability",
      color: partnerPrimaryColor,
    },
  }), [partnerPrimaryColor]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="w-full h-full flex items-center justify-center"
    >
      <ChartContainer 
        config={chartConfig} 
        className="mx-auto w-full max-w-4xl"
        style={{ aspectRatio: '16/10' }}
      >
        <RadarChart data={chartData}>
          <ChartTooltip 
            cursor={false} 
            content={<ChartTooltipContent hideLabel />} 
          />
          <PolarGrid 
            stroke={partnerPrimaryColor}
            strokeWidth={1.5}
            strokeOpacity={0.3}
            gridType="polygon"
            radialLines={true}
          />
          <PolarAngleAxis dataKey="category" />
          <PolarRadiusAxis 
            angle={90} 
            domain={[0, 100]} 
            tick={false}
            axisLine={false}
          />
          <Radar
            dataKey="value"
            fill="var(--color-value)"
            fillOpacity={0.6}
            stroke="var(--color-value)"
            strokeWidth={2.5}
          />
        </RadarChart>
      </ChartContainer>
    </motion.div>
  );
};

